import * as THREE from 'three';
import { DigitalObject } from '../../rendering/binary/DigitalObject.js';
import { blockStride } from '../streaming/worldGrid.js';

/**
 * Red binary ground vehicle (body aligned along local +Z).
 */
export class RoadVehicle {
  /**
   * @param {{
   *   material: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   seed: number;
   *   cellIdBase: number;
   *   config: import('../../config.js').CONFIG['roadVehicles'];
   *   cityConfig: import('../../config.js').CONFIG['city'];
   *   index: number;
   * }} options
   */
  constructor({ material, seed, cellIdBase, config, cityConfig, index }) {
    this.config = config;
    this.cityConfig = cityConfig;
    this.root = new THREE.Group();
    this.root.name = 'RoadVehicle';

    const parts = [
      {
        shape: 'box',
        size: [1.75, 0.52, 3.45],
        position: [0, 0.26, 0],
        cellsPerMeter: 5,
        name: 'Body',
      },
      {
        shape: 'facade',
        size: [1.42, 0.46, 1.35],
        position: [0, 0.62, 0.55],
        cellsPerMeter: 5,
        name: 'Cabin',
      },
      {
        shape: 'box',
        size: [1.85, 0.12, 0.55],
        position: [0, 0.12, 1.35],
        cellsPerMeter: 4,
        name: 'BumperFront',
      },
      {
        shape: 'box',
        size: [0.38, 0.32, 0.38],
        position: [-0.72, 0.1, 1.05],
        cellsPerMeter: 3,
        name: 'WheelFL',
      },
      {
        shape: 'box',
        size: [0.38, 0.32, 0.38],
        position: [0.72, 0.1, 1.05],
        cellsPerMeter: 3,
        name: 'WheelFR',
      },
      {
        shape: 'box',
        size: [0.38, 0.32, 0.38],
        position: [-0.72, 0.1, -1.05],
        cellsPerMeter: 3,
        name: 'WheelRL',
      },
      {
        shape: 'box',
        size: [0.38, 0.32, 0.38],
        position: [0.72, 0.1, -1.05],
        cellsPerMeter: 3,
        name: 'WheelRR',
      },
    ];

    let cellOffset = cellIdBase;
    let maxColumns = 1;
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      const body = new DigitalObject({
        material,
        shape: part.shape,
        size: part.size,
        position: part.position,
        cellsPerMeter: part.cellsPerMeter,
        seed: seed + i * 53,
        cellIdBase: cellOffset,
        name: part.name,
      });
      cellOffset += body.cellsUsed;
      for (const surface of body.surfaces) {
        maxColumns = Math.max(maxColumns, surface.columns);
      }
      this.root.add(body.group);
    }
    this.cellsUsed = cellOffset - cellIdBase;
    this.maxColumns = maxColumns;

    const hash = (n) => ((seed * 1103515245 + n * 12345) >>> 0) / 4294967296;
    const t = (min, max, n) => min + (max - min) * hash(n);

    /** @type {'alongZ' | 'alongX'} */
    this.axis = hash(1) > 0.5 ? 'alongZ' : 'alongX';
    this.laneOffset = Math.floor(hash(2) * 5) - 2 + (index % 3);
    this.travel = t(-0.5, 0.5, 3) * config.travelSpan;
    this.direction = hash(4) > 0.5 ? 1 : -1;
    this.speed = t(config.speedMin, config.speedMax, 5);
    this._phase = hash(6) * 10;
  }

  /**
   * @param {number} delta
   * @param {number} anchorX
   * @param {number} anchorZ
   */
  update(delta, anchorX, anchorZ) {
    const { roadWidth } = this.cityConfig;
    const stride = blockStride(this.cityConfig);
    const roadCenter = roadWidth / 2;
    const half = this.config.travelSpan * 0.5;

    this.travel += this.speed * delta * this.direction;
    if (this.travel > half) {
      this.travel = -half;
    } else if (this.travel < -half) {
      this.travel = half;
    }

    this._phase += delta;

    if (this.axis === 'alongZ') {
      const roadIdx = Math.round((anchorX - roadCenter) / stride) + this.laneOffset;
      const x = roadIdx * stride + roadCenter;
      const z = anchorZ + this.travel;
      this.root.position.set(x, this.config.rideHeight, z);
      this.root.rotation.y = this.direction > 0 ? 0 : Math.PI;
    } else {
      const roadIdx = Math.round((anchorZ - roadCenter) / stride) + this.laneOffset;
      const z = roadIdx * stride + roadCenter;
      const x = anchorX + this.travel;
      this.root.position.set(x, this.config.rideHeight, z);
      this.root.rotation.y = this.direction > 0 ? -Math.PI / 2 : Math.PI / 2;
    }

    const bob = Math.sin(this._phase * 14 + this.speed) * 0.012;
    this.root.position.y = this.config.rideHeight + bob;
  }

  dispose() {
    this.root.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
      }
    });
    this.root.clear();
  }
}
